import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { AuthProvider } from './contexts/AuthContext'
import ErrorBoundary from './components/ErrorBoundary'
import { getInFlightWrites } from './lib/api'
import './index.css'

/**
 * Auto-recover from stale-chunk errors after a deploy.
 *
 * When we ship a new frontend, Vite emits differently-hashed JS chunks
 * (`NewSale-XYZ.js`). Any tab open with the OLD `index.html` is still
 * running JS that references the OLD hashes. When the user navigates to
 * a lazy-loaded route the old bundle tries `import('/assets/oldHash.js')`,
 * the CDN 404s (or the SW misses), and React shows a blank screen with
 * "Failed to fetch dynamically imported module" in the console.
 *
 * There's no correct in-tab recovery — the old bundle can't magically
 * resolve to new code. The safe response is a one-shot hard reload
 * that pulls the new index.html (network-first per sw.js) and the
 * matching chunks.
 *
 * The sessionStorage flag protects against a reload loop if the deploy
 * itself is broken and every load fails on the same import.
 */
function installChunkRecoverer() {
  const sentinel = 'vansales:chunk-recovery'
  const isStaleChunkError = (msg: string) =>
    /Failed to fetch dynamically imported module|Loading chunk \d+ failed|error loading dynamically imported module/i.test(msg)
  const attemptReload = (msg: string) => {
    if (!isStaleChunkError(msg)) return false
    if (sessionStorage.getItem(sentinel)) {
      console.error('[chunk-recovery] Reload already attempted this session; not looping.')
      return false
    }
    sessionStorage.setItem(sentinel, '1')
    console.warn('[chunk-recovery] Stale chunk detected — reloading to pull new bundle.')
    window.location.reload()
    return true
  }
  window.addEventListener('error', (e) => attemptReload(e.message || ''))
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || String(e.reason || '')
    attemptReload(msg)
  })
  // Once the new bundle loads without a chunk error, clear the sentinel
  // so future stale-chunk errors (from the NEXT deploy) can still recover.
  window.addEventListener('load', () => sessionStorage.removeItem(sentinel))
}
installChunkRecoverer()

// Register Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        // When a new SW finishes installing and we already have a controller,
        // a new app version is waiting. Fire an app-level event so the UI can
        // surface a "Reload to update" banner (handled by UpdateBanner).
        const notifyIfWaiting = () => {
          if (registration.waiting && navigator.serviceWorker.controller) {
            window.dispatchEvent(new CustomEvent('sw:update-available'))
          }
        }

        // Case 1: a new version was already installed before this tab loaded.
        notifyIfWaiting()

        // Case 2: a new version installs while the tab is open.
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                window.dispatchEvent(new CustomEvent('sw:update-available'))
              }
            })
          }
        })

        // After the new SW takes over (user clicked Reload), refresh the page
        // so the new bundle is actually loaded. Without this, the tab keeps
        // running the old code even though the SW has updated.
        //
        // Critical: defer the reload if a write (sale, payment, etc.) is
        // still in flight. Otherwise the in-progress request is aborted by
        // the navigation and the user's transaction silently vanishes.
        let refreshing = false
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (refreshing) return
          refreshing = true
          const tryReload = () => {
            if (getInFlightWrites() === 0) {
              window.location.reload()
            } else {
              // Re-check after the next idle event (or in 200ms as a
              // safety net in case the event fired before we listened).
              setTimeout(tryReload, 200)
            }
          }
          tryReload()
        })
      })
      .catch((error) => {
        console.error('[App] Service Worker registration failed:', error)
      })
  })
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
)
